<template>
  <!-- Introduced with MPC-8377
  Filter location is a special filter that was inspired by Overseas location filter -->
  <div class="emh-filter-location">
    <fieldset>
      <div class="emh-filter-location__form" :class="{ '-display-column': displayColumn, '-disabled': isLoading }">
        <input-autocomplete
          class="emh-filter-location__postal-search"
          :label="t('dcp.srp.filters.locationSearch.inputText')"
          :search="search"
          :autosuggestions="autosuggestions"
          :loading="autosuggestionsLoading"
          :not-found-label="t('dcp.srp.filters.locationSearch.autosuggestions.noResults')"
          @search-change="onSearchChange"
          @search-focus="onSearchFocus"
          @suggestion-select="onSuggestionSelected"
          @reset="onReset"
        ></input-autocomplete>

        <ui-dropdown
          class="emh-filter-location__distance"
          :label="t('dcp.srp.filters.locationSearch.distanceFrom')"
          :value="distance"
          :options="distanceOptions"
          @change="onDistanceSelectChange"
        />
      </div>
    </fieldset>

    <p
      v-show="selectedFilterParams"
      class="emh-filter-location__hint wb-type-hint"
      :class="{ 'emh-filter-location__hint--error': noResults }"
      v-html="hintMessage"
    ></p>
  </div>
</template>

<script lang="ts">
import InputAutocomplete, {
  Autosuggestion,
  Autosuggestions,
} from '@dh-io-emh/shared/src/components/utils/inputAutocomplete/index.vue';
import { useI18n } from '@dh-io-emh/shared/src/composables/i18n.composable';
import { infuse } from '@dh-io-emh/shared/src/ioc-config';
import { IDealerBase } from '@dh-io-emh/shared/src/models/dealer.model';
import { IProductFilterOptionDTO } from '@dh-io-emh/shared/src/models/product-filter/product-filter-base.model';
import { IProductFilterType } from '@dh-io-emh/shared/src/models/product-filter/product-filter.model';
import { Logger } from '@dh-io-emh/shared/src/utils/logger.util';
import { DropdownOption } from '@dh-io-emh/ui/src/form/dropdown/dropdown.modal';
import UiDropdown from '@dh-io-emh/ui/src/form/dropdown/Dropdown.vue';
import { computed, ComputedRef, defineComponent, PropType, Ref, ref, toRefs, watch } from 'vue';
import { LocationFilterParams } from '../../composables/locations-filter.composable';

export default defineComponent({
  name: 'EmhFilterLocation',

  components: {
    UiDropdown,
    InputAutocomplete,
  },

  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    selectedFilterParams: {
      type: Object as PropType<LocationFilterParams>,
    },
    filter: {
      type: Object as PropType<IProductFilterType>,
      required: true,
    },
    allowedList: {
      type: Array as PropType<Array<string>>,
      default: () => [],
    },
    displayColumn: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['params-updated', 'vendors-selected', 'reset'],

  setup(props, { emit }) {
    const {
      dealerLocator,
      shopCountry,
      srp: { locationFilter },
    } = infuse('config');

    const dealerApi = infuse('dealerApi');

    const { t } = useI18n();

    const country: string = dealerLocator.country || shopCountry;
    const distanceOptions: DropdownOption[] = (locationFilter?.options ?? []).map((option) => ({
      value: option,
      label: option,
    }));

    const { filter, selectedFilterParams } = toRefs(props);
    const autosuggestions: Ref<Autosuggestions> = ref([]);
    const selectedSuggestion: Ref<Autosuggestion | undefined> = ref(undefined);
    const autosuggestionsLoading = ref(false);
    const search = ref('');
    const distance = ref('');
    const requestedVendors: Ref<string[]> = ref([]);

    const emits = {
      paramsUpdated: (id: string, search: string, distance: string) => emit('params-updated', { id, search, distance }),
      vendorsSelected: (vendors: string[] = []) => emit('vendors-selected', vendors),
      reset: (): void => emit('reset'),
    };

    /**
     * Get autosuggestions on input-change if it isn't empty
     * and reset everything in other case
     */
    const onSearchChange = (value: string): void => {
      search.value = value;

      if (value) {
        getAutosuggestions(value);
      } else {
        emits.reset();
        resetFilterRefs();
      }
    };

    /**
     * Get autosuggestions on input-focus if there aren't any now in the state
     */
    const onSearchFocus = (): void => {
      if (search.value && !autosuggestions.value.length) {
        getAutosuggestions(search.value);
      }
    };

    /**
     * Update the search property, set selected suggestion
     * and get all possible vendors for the current locationId and radius selection
     */
    const onSuggestionSelected = (suggestion: Autosuggestion): void => {
      search.value = suggestion.label;
      selectedSuggestion.value = suggestion;

      getAutosuggestions(search.value);
      getPossibleVendors(selectedSuggestion.value, distance.value);
    };

    /**
     * Changes the distanceFrom parameter
     * that is passed to the DealerApi.
     */
    const onDistanceSelectChange = (value: string): void => {
      distance.value = value;

      if (selectedSuggestion.value) {
        getPossibleVendors(selectedSuggestion.value, distance.value);
      } else if (search.value === selectedFilterParams?.value?.search) {
        getPossibleVendors(
          {
            id: selectedFilterParams.value.id || '',
            label: selectedFilterParams.value.search || '',
          },
          value,
        );
      }
    };

    /**
     * Emit event to the parent component to reset location filter and current dealer selection
     */
    const onReset = (): void => {
      emits.reset();
      resetFilterRefs();
    };

    /**
     * TODO: move to the search store
     * Make an API request to the DealerApi to get all vendors
     * for the current locationId and radius
     */
    const getPossibleVendors = async (suggestion: Autosuggestion, distance: string): Promise<void> => {
      try {
        const { results } = await dealerApi.getDealersByLocation(suggestion.id, distance, country);

        // Get only that dealers, who are provided in the location options from the product API
        const availableVendors: string[] = (results || [])
          .map(({ baseInfo }: IDealerBase) => baseInfo.externalId)
          // remove dealers without vehicles in the SRP results
          .filter((dealerId: string) =>
            (((props.filter as IProductFilterType)?.options as IProductFilterOptionDTO[]) ?? [])?.find(
              (option) => option.value === dealerId,
            ),
          );

        // Check if there are any available products for the found dealers
        // and assign the availableVendors to the requestedVendors only in case if there is one
        // available product or more
        requestedVendors.value = props.allowedList.filter((dealer) => availableVendors.includes(dealer)).length
          ? availableVendors
          : [];

        emits.paramsUpdated(suggestion.id, suggestion.label, distance);

        if (requestedVendors.value.length > 0) {
          emits.vendorsSelected(requestedVendors.value);
        }
      } catch (error) {
        Logger.error('FilterLocation::getPossibleVendors() --> error', error);
      }
    };

    /**
     * Get autosuggestions from the DealerApi with provided search term
     */
    const getAutosuggestions = async (search: string): Promise<void> => {
      autosuggestionsLoading.value = true;

      try {
        const { suggestions } = await dealerApi.getAutosuggestions(search, country);

        autosuggestions.value = suggestions.map((suggestion) => ({
          id: suggestion.locationId,
          label: suggestion.label,
        }));
      } catch (error) {
        Logger.error('FilterLocation::getAutosuggestions() --> error', error);
      }

      autosuggestionsLoading.value = false;
    };

    /**
     * Reset search filter ref properties
     */
    const resetFilterRefs = (): void => {
      search.value = '';
      autosuggestions.value = [];
      selectedSuggestion.value = undefined;
    };

    /**
     * Getter to set an error state of the hint in case of lack of fittable vendors
     */
    const noResults: ComputedRef<boolean> = computed(() => {
      return requestedVendors.value.length === 0;
    });

    /**
     * There are two types of the hint message:
     * - no results
     * - "this number of dealers was found"
     */
    const hintMessage: ComputedRef<string> = computed(() => {
      return noResults.value
        ? t('dcp.srp.filters.locationSearch.noResults', {
            inputText: search.value,
            countryName: t('dcp.country.name'),
          })
        : t('dcp.srp.filters.locationSearch.results', {
            dealerNumberFound: requestedVendors.value.length,
          });
    });

    /**
     * Update requestedVendors array if new vendors were provided in Store
     */
    watch(
      filter,
      (newValue) => {
        requestedVendors.value = newValue.value || [];
      },
      { immediate: true },
    );

    /**
     * Update search term and distance if they were provided in Store
     * and reset all references if the search param was cleared
     */
    watch(
      selectedFilterParams as Ref<LocationFilterParams>,
      (params: LocationFilterParams | undefined): void => {
        distance.value = params?.distance || distanceOptions[0].value;

        if (params?.search) {
          search.value = params.search;
        } else {
          resetFilterRefs();
        }
      },
      { immediate: true },
    );

    return {
      autosuggestions,
      search,
      distance,
      distanceOptions,
      autosuggestionsLoading,
      noResults,
      hintMessage,

      onDistanceSelectChange,
      onSearchChange,
      onSearchFocus,
      onSuggestionSelected,
      onReset,

      t,
    };
  },
});
</script>

<style lang="scss" src="./filterLocation.scss"></style>
